import type {
	CAButtonType,
	CANotificationItem,
	CANotificationType,
} from '@ds/interfaces'
import { NotificationService } from '@ds/services/NotificationService'
import { DELAY } from '@vuejs/helpers/_constants'
import { defineComponent, type PropType } from 'vue'

import CAButton from '../buttons/CAButton'
import CACloseButton from '../buttons/CACloseButton'
import CAIcon from '../shared/CAIcon'

/**
 * Notification component
 *
 * @component CANotification
 *
 * @prop {CANotificationItem} notification - The notification item to display
 *
 * @emit action - Emitted when the action button is clicked
 * @emit close - Emitted when the close button is clicked
 */
export default defineComponent({
	name: 'CANotification',

	props: {
		/**
		 *
		 * The notification item to display
		 * @default {}
		 * @type {CANotificationItem}
		 * @required
		 *
		 */
		notification: { required: true, type: Object as PropType<CANotificationItem> },
	},

	emits: [
		'action',
		'close',
	],

	data(): Data {
		return {
			isPaused: false,
			message: this.notification,
			remainingTime: 0,
			startTime: 0,
			timeoutId: 0,
		}
	},

	mounted() {
		this.setupAutoCloseTimers()
	},

	beforeUnmount() {
		if (this.timeoutId) {
			window.clearTimeout(this.timeoutId)
		}
	},

	watch: {
		notification: {
			handler() {
				this.message = this.notification
				this.setupAutoCloseTimers()
			},
			deep: true,
		},
	},

	methods: {
		pauseTimer() {
			if (this.timeoutId && !this.isPaused) {
				const elapsedTime = Date.now() - this.startTime

				this.remainingTime = Math.max(0, this.remainingTime - elapsedTime)

				window.clearTimeout(this.timeoutId)
				this.timeoutId = 0
				this.isPaused = true
			}
		},

		resumeTimer() {
			if (this.isPaused && this.remainingTime > 0) {
				this.isPaused = false
				this.startTimer()
			}
		},

		setupAutoCloseTimers() {
			if (this.timeoutId) {
				window.clearTimeout(this.timeoutId)
			}

			this.isPaused = false
			this.remainingTime = 0
			this.startTime = 0
			this.timeoutId = 0

			const duration = !!this.message.buttonText ? DELAY.NINE_SECONDS : this.message.duration
			const actualDuration = duration || DELAY.FIVE_SECONDS

			this.remainingTime = actualDuration
			this.startTimer()
		},

		setButtonType(type: CANotificationType): CAButtonType {
			switch (type) {
				case 'error':
					return 'danger'

				case 'info':
					return 'primary'

				case 'success':
					return 'success'

				default:
					return 'info'
			}
		},

		startTimer() {
			this.startTime = Date.now()
			this.timeoutId = window.setTimeout(() => {
				const notificationService = NotificationService.useNotifications()

				notificationService.removeNotification(this.message)
			}, this.remainingTime)
		},
	},

	render() {
		const notificationService = NotificationService.useNotifications()

		return (
			<div
				aria-modal="true"
				class={[
					'ca-notification',
					`ca-notification__${this.message.type}`,
					{
						'ca-notification__with-close-button': this.message.showCloseButton,
						'ca-notification__with-button': !!this.message.buttonText,
					},
				]}
				data-test={`ca-notification-${this.message.type}`}
				role="dialog"
				onMouseenter={this.pauseTimer}
				onMouseleave={this.resumeTimer}
			>
				<div class="ca-notification-wrapper">
					<div class="ca-notification__svg-wrapper">
						{this.message.type === 'default' &&
							<CAIcon class="svg-success" icon="SVGCheckCircleFilled" />
						}

						{this.message.type === 'error' &&
							<CAIcon class="svg-error" icon="SVGAlertShieldFilled" />
						}

						{this.message.type === 'info' &&
							<CAIcon class="svg-info" icon="SVGInfoFilled" />
						}

						{this.message.type === 'success' &&
							<CAIcon class="svg-success" icon="SVGCheckCircleFilled" />
						}
					</div>

					<div class="ca-notification__message-wrapper">
						<p class="ca-notification__message" data-test="ca-notification-message">
							{this.message.message}
						</p>

						{this.message.buttonText &&
							<CAButton
								buttonStyle="text"
								dataTest="ca-notification-button"
								size="compact"
								text={this.message.buttonText}
								type={this.setButtonType(this.message.type)}
								onClick={() => this.$emit('action')}
							/>
						}
					</div>

					{this.message.showCloseButton &&
						<CACloseButton onClose={() => notificationService.removeNotification(this.message)} />
					}
				</div>
			</div>
		)
	},
})

interface Data {
	isPaused: boolean
	message: CANotificationItem
	remainingTime: number
	startTime: number
	timeoutId: number
}
